import React, { useEffect, useState } from 'react';
import { Check, Crown, Palette, RotateCcw, Save, ShieldCheck, Sparkles, X } from 'lucide-react';
import {
  CURATED_GUILD_COSMETICS_POLICY,
  focalPointToCssPosition,
  scryfallArtCropUrl,
  type ClientGameView,
  type GameIntent,
  type PlaymatFocalPoint,
} from '#shared';
import { playtableApi, type SavedDeckDTO } from '../lib/clientApi.ts';
import { CardSleeveBack } from './CardVisual.tsx';

interface CosmeticsGalleryModalProps {
  view: ClientGameView;
  onDispatch: (intent: GameIntent) => void;
  onOpenDeckLoader: () => void;
  onClose: () => void;
}

export function CosmeticsGalleryModal({
  view,
  onDispatch,
  onOpenDeckLoader,
  onClose,
}: CosmeticsGalleryModalProps) {
  const mySeat = view.viewerSeatId ? view.seats[view.viewerSeatId] : null;
  const legalPool = mySeat?.deckMeta?.legalCosmeticArt ?? [];

  const [selectedPlaymatCard, setSelectedPlaymatCard] = useState<string | null>(
    mySeat?.playmatCardName ?? null
  );
  const [selectedSleeveCard, setSelectedSleeveCard] = useState<string | null>(
    mySeat?.sleeveCardName ?? null
  );
  const [focalPoint, setFocalPoint] = useState<PlaymatFocalPoint>(
    mySeat?.playmatFocalPoint ?? 'center'
  );
  const [filterKind, setFilterKind] = useState<'all' | 'commander' | 'deck'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [savedDecks, setSavedDecks] = useState<SavedDeckDTO[]>([]);
  const [saveFeedback, setSaveFeedback] = useState<string | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  useEffect(() => {
    playtableApi
      .listSavedDecks()
      .then((res) => setSavedDecks(res.decks))
      .catch(() => {});
  }, []);

  const previewPlaymatUrl = selectedPlaymatCard ? scryfallArtCropUrl(selectedPlaymatCard) : null;
  const previewSleeveUrl = selectedSleeveCard ? scryfallArtCropUrl(selectedSleeveCard) : null;

  const filteredArt = legalPool.filter((opt) => {
    if (filterKind === 'commander' && !opt.isCommander) return false;
    if (filterKind === 'deck' && opt.isCommander) return false;
    if (searchQuery.trim() && !opt.cardName.toLowerCase().includes(searchQuery.trim().toLowerCase())) {
      return false;
    }
    return true;
  });

  const applyToSeat = (
    nextPlaymat: string | null = selectedPlaymatCard,
    nextSleeve: string | null = selectedSleeveCard,
    nextFocal: PlaymatFocalPoint = focalPoint
  ) => {
    setSelectedPlaymatCard(nextPlaymat);
    setSelectedSleeveCard(nextSleeve);
    setFocalPoint(nextFocal);
    onDispatch({
      type: 'SET_SEAT_COSMETICS',
      playmatCardName: nextPlaymat,
      sleeveCardName: nextSleeve,
      playmatFocalPoint: nextFocal,
    });
  };

  const handleSaveLoadoutToDeck = async () => {
    if (!mySeat?.deckMeta?.deckId) {
      setSaveFeedback('Save this deck in Deck Manager first to persist cosmetics per deck.');
      return;
    }
    const matchingSaved = savedDecks.find((d) => d.deckId === mySeat.deckMeta?.deckId);
    if (!matchingSaved) {
      setSaveFeedback('Deck is not in your Saved Library yet — save it in Deck Manager first.');
      return;
    }
    try {
      await playtableApi.saveDeck({
        deckId: matchingSaved.deckId,
        deck: matchingSaved.deck,
        isPublic: matchingSaved.isPublic,
        cosmetics: {
          sleeveCardName: selectedSleeveCard,
          playmatCardName: selectedPlaymatCard,
          focalPoint,
        },
      });
      setSaveFeedback(`Saved sleeve, playmat & ${focalPoint} focal point to "${matchingSaved.name}"!`);
      setTimeout(() => setSaveFeedback(null), 3200);
    } catch (err: any) {
      setSaveFeedback(err.message ?? 'Could not save deck loadout.');
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Deck-Identity Card-Art Playmats and Sleeves"
      onClick={onClose}
      className="fixed inset-0 z-[105] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 overscroll-contain"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl max-h-[90vh] rounded-2xl bg-slate-950 border border-slate-800 shadow-2xl flex flex-col overflow-hidden overscroll-contain"
      >
        <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <Palette className="w-4 h-4 text-amber-400" />
            <h2 className="font-extrabold text-sm text-slate-100">
              Playmat & Card Sleeves — Official Deck-Identity Art Gallery
            </h2>
          </div>
          <button
            type="button"
            aria-label="Close cosmetics modal"
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto overscroll-contain space-y-4 text-xs text-slate-200">
          {/* Content Safety & Player Distinguishability Policy Banner */}
          <div className="p-3 rounded-xl bg-emerald-950/35 border border-emerald-500/35 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-start gap-2.5">
              <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <div className="font-bold text-emerald-200">
                  Safe Tabletop Policy · Zero External Image Uploads
                </div>
                <p className="text-[11px] text-slate-300 leading-relaxed">
                  Playmats and sleeves use official Scryfall card art from your loaded deck & color identity (
                  {CURATED_GUILD_COSMETICS_POLICY.allowExternalUploads
                    ? 'uploads enabled'
                    : 'external URLs disabled'}
                  ). Unset sleeves use the classic 5-pip card back, and unset playmats auto-assign distinct
                  deck-identity colors across all 1–8 seats so every player is unmistakably clear at a glance.
                </p>
              </div>
            </div>
          </div>

          {/* Live Side-by-Side Tabletop Contrast & Sleeve Preview */}
          {mySeat && (
            <div className="grid grid-cols-1 lg:grid-cols-[1.35fr_0.65fr] gap-3">
              {/* Ultra PRO Zone Contrast + Focal Point Live Preview */}
              <div className="rounded-xl bg-slate-900/80 border border-slate-800 p-3 space-y-2.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="font-bold text-white">Live Playmat & Ultra PRO Zone Contrast</div>
                    <div className="text-[10px] text-slate-400">
                      {selectedPlaymatCard
                        ? `Card Art: ${selectedPlaymatCard} (${focalPoint.toUpperCase()} crop)`
                        : `Default Distinct Identity Mat (${
                            mySeat.deckMeta?.colorIdentity.join('') || 'Colorless / Auto'
                          })`}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {(['top', 'center', 'bottom'] as const).map((fp) => (
                      <button
                        key={fp}
                        type="button"
                        disabled={!selectedPlaymatCard}
                        onClick={() => applyToSeat(selectedPlaymatCard, selectedSleeveCard, fp)}
                        className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase border disabled:opacity-40 ${
                          focalPoint === fp
                            ? 'bg-amber-500 text-slate-950 border-amber-400'
                            : 'bg-slate-950 text-slate-300 border-slate-700 hover:text-white'
                        }`}
                      >
                        {fp}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => applyToSeat(null, selectedSleeveCard, 'center')}
                      className="px-2 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-[10px] font-semibold flex items-center gap-1"
                    >
                      <RotateCcw className="w-3 h-3" />
                      <span>Identity Mat</span>
                    </button>
                  </div>
                </div>

                <div
                  style={
                    previewPlaymatUrl
                      ? {
                          backgroundImage: `linear-gradient(rgba(9,13,22,0.52), rgba(9,13,22,0.74)), url(${previewPlaymatUrl})`,
                          backgroundSize: 'cover',
                          backgroundPosition: focalPointToCssPosition(focalPoint),
                          borderColor: mySeat.matBorderCss,
                        }
                      : {
                          background: mySeat.matBgCss,
                          borderColor: mySeat.matBorderCss,
                        }
                  }
                  className="h-36 rounded-xl border-2 p-2.5 flex flex-col justify-between shadow-inner"
                >
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="inline-flex items-center gap-1.5 font-extrabold text-white">
                      <span
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: mySeat.profileColor }}
                      />
                      {mySeat.displayName}
                    </span>
                    <span className="px-2 py-0.5 rounded bg-black/55 border border-white/15 font-mono text-amber-300">
                      {mySeat.life} LIFE
                    </span>
                  </div>
                  <div className="border-t border-b border-white/15 py-1.5 text-[8px] font-bold uppercase tracking-[0.16em] text-white/55">
                    Creatures & Permanents — Zone Line Contrast Preview
                  </div>
                  <div className="text-[8px] font-bold uppercase tracking-[0.16em] text-white/55">
                    Lands · Command · Graveyard
                  </div>
                </div>
              </div>

              {/* Side-by-Side Regular Magic Card Back vs Chosen Card-Art Sleeve */}
              <div className="rounded-xl bg-slate-900/80 border border-slate-800 p-3 flex flex-col justify-between gap-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-white">Deck Sleeves</div>
                    <div className="text-[10px] text-slate-400 truncate">
                      {selectedSleeveCard ? selectedSleeveCard : 'Regular Magic Card Back'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => applyToSeat(selectedPlaymatCard, null, focalPoint)}
                    className="px-2 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-[10px] font-semibold"
                  >
                    Magic Back
                  </button>
                </div>

                <div className="flex items-center justify-around py-1">
                  <div className="flex flex-col items-center gap-1">
                    <CardSleeveBack
                      sleeveUrl={null}
                      accentColor={mySeat.profileColor}
                      size="sm"
                    />
                    <span className="text-[9px] text-slate-400">Magic Card Back</span>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <CardSleeveBack
                      sleeveUrl={previewSleeveUrl}
                      accentColor={mySeat.profileColor}
                      size="sm"
                    />
                    <span className="text-[9px] text-amber-300 font-bold">Active Sleeve</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleSaveLoadoutToDeck}
                  className="w-full py-1.5 px-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-[11px] flex items-center justify-center gap-1.5"
                >
                  <Save className="w-3.5 h-3.5" />
                  <span>Save Loadout to Library Deck</span>
                </button>
              </div>
            </div>
          )}

          {saveFeedback && (
            <div className="px-3 py-2 rounded-xl bg-indigo-950/70 border border-indigo-400/50 text-indigo-200 font-semibold text-[11px]">
              {saveFeedback}
            </div>
          )}

          {/* Deck-Identity Legal Card-Art Visual Thumbnail Grid */}
          {mySeat ? (
            mySeat.streamMode ? (
              <div className="p-4 rounded-xl bg-slate-900 border border-rose-500/40 text-center space-y-2">
                <div className="font-bold text-rose-200">
                  Stream Mode is active on this broadcast window
                </div>
                <p className="text-slate-400 text-[11px]">
                  Your maindeck card-art pool is hidden on the broadcast stream so viewers cannot deduce your
                  library list. Open your Private Hand Companion window or pause Stream Mode in lobby to browse
                  all deck art.
                </p>
              </div>
            ) : legalPool.length === 0 ? (
              <div className="p-5 rounded-xl bg-slate-900/70 border border-dashed border-slate-700 text-center space-y-2.5">
                <Sparkles className="w-5 h-5 text-amber-400 mx-auto" />
                <div className="font-bold text-slate-200">
                  Load a deck first to unlock its official Scryfall card-art playmats & sleeves
                </div>
                <p className="text-[11px] text-slate-400">
                  Until you pick card art, your seat uses the classic 5-pip card back and a unique color-coded
                  playmat distinct from every opponent.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onOpenDeckLoader();
                  }}
                  className="px-4 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold"
                >
                  Open Deck Manager
                </button>
              </div>
            ) : (
              <div className="space-y-2.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-1">
                    {(
                      [
                        ['all', `All Legal Art (${legalPool.length})`],
                        ['commander', 'Commanders'],
                        ['deck', 'Deck & Identity Lands'],
                      ] as const
                    ).map(([id, label]) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setFilterKind(id)}
                        className={`px-2.5 py-1 rounded-lg font-bold text-[10px] border ${
                          filterKind === id
                            ? 'bg-violet-600 border-violet-400 text-white'
                            : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Filter deck card art..."
                    className="px-2.5 py-1 rounded-lg bg-slate-900 border border-slate-700 text-xs w-48"
                  />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5 max-h-64 overflow-y-auto pr-1">
                  {filteredArt.map((opt) => {
                    const isMat = selectedPlaymatCard?.toLowerCase() === opt.cardName.toLowerCase();
                    const isSleeve = selectedSleeveCard?.toLowerCase() === opt.cardName.toLowerCase();
                    return (
                      <div
                        key={opt.cardName}
                        className={`rounded-xl border overflow-hidden bg-slate-900 flex flex-col justify-between transition-colors ${
                          isMat || isSleeve
                            ? 'border-amber-400 ring-1 ring-amber-400/50'
                            : 'border-slate-800 hover:border-slate-600'
                        }`}
                      >
                        <div className="relative h-20 bg-slate-950 overflow-hidden">
                          <img
                            src={opt.artCropUrl}
                            alt={opt.cardName}
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                          {opt.isCommander && (
                            <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-amber-500/90 text-slate-950 font-extrabold text-[8px] inline-flex items-center gap-0.5">
                              <Crown className="w-2.5 h-2.5" />
                              CMDR
                            </span>
                          )}
                          {(isMat || isSleeve) && (
                            <span className="absolute top-1 right-1 px-1.5 py-0.5 rounded bg-emerald-500 text-slate-950 font-extrabold text-[8px] inline-flex items-center gap-0.5">
                              <Check className="w-2.5 h-2.5" />
                              {isMat && isSleeve ? 'MAT+SLV' : isMat ? 'MAT' : 'SLEEVE'}
                            </span>
                          )}
                        </div>
                        <div className="p-2 space-y-1.5">
                          <div className="font-bold text-[11px] text-slate-100 truncate" title={opt.cardName}>
                            {opt.cardName}
                          </div>
                          <div className="grid grid-cols-2 gap-1">
                            <button
                              type="button"
                              onClick={() =>
                                applyToSeat(isMat ? null : opt.cardName, selectedSleeveCard, focalPoint)
                              }
                              className={`py-1 rounded text-[10px] font-bold border ${
                                isMat
                                  ? 'bg-amber-500 text-slate-950 border-amber-400'
                                  : 'bg-slate-950 hover:bg-slate-800 border-slate-700 text-slate-200'
                              }`}
                            >
                              {isMat ? 'Mat ✓' : 'Set Mat'}
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                applyToSeat(selectedPlaymatCard, isSleeve ? null : opt.cardName, focalPoint)
                              }
                              className={`py-1 rounded text-[10px] font-bold border ${
                                isSleeve
                                  ? 'bg-violet-500 text-white border-violet-400'
                                  : 'bg-slate-950 hover:bg-slate-800 border-slate-700 text-slate-200'
                              }`}
                            >
                              {isSleeve ? 'Sleeve ✓' : 'Sleeve'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )
          ) : (
            <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 text-center">
              Spectators view player cosmetics live on the tabletop. Take an open seat to customize your own
              playmat & sleeves.
            </div>
          )}

          {/* Pod Distinguishability Swatches (1-8 Seats) & Host Per-Seat Reset */}
          <div className="pt-3 border-t border-slate-800 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-bold text-slate-100">
                  Pod Player-Distinguishability Map ({view.seatOrder.length} Seats)
                </div>
                <div className="text-[10px] text-slate-400">
                  Auto-deduplicated seat accents & mats ensure players sharing the same commander or color
                  identity never blend together.
                </div>
              </div>
              {(view.isHost || view.isJudge) && (
                <label className="flex items-center gap-2 px-2.5 py-1 rounded-lg bg-slate-900 border border-slate-700 cursor-pointer text-[11px]">
                  <span className="text-slate-200 font-semibold">Force Default Cosmetics Table-Wide</span>
                  <input
                    type="checkbox"
                    checked={view.settings.forceDefaultCosmetics}
                    onChange={(e) =>
                      onDispatch({
                        type: 'UPDATE_TABLE_SETTINGS',
                        patch: { forceDefaultCosmetics: e.target.checked },
                      })
                    }
                    className="accent-amber-500"
                  />
                </label>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
              {view.seatOrder.map((sid, idx) => {
                const s = view.seats[sid];
                if (!s) return null;
                const canResetSeat = view.isHost || view.isJudge || view.viewerSeatId === sid;
                const hasCustom = Boolean(s.playmatCardName || s.sleeveCardName);
                return (
                  <div
                    key={sid}
                    style={{ background: s.matBgCss, borderColor: s.matBorderCss }}
                    className="p-2 rounded-xl border flex items-center justify-between gap-2"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0 border border-black/40"
                          style={{ backgroundColor: s.profileColor }}
                        />
                        <span className="font-mono text-[9px] px-1 rounded bg-black/50 text-slate-200">
                          S{idx + 1}
                        </span>
                        <span className="font-bold text-white truncate text-[11px]">{s.displayName}</span>
                      </div>
                      <div className="text-[9px] text-slate-300/85 truncate mt-0.5">
                        {s.playmatCardName ? `Mat: ${s.playmatCardName}` : 'Distinct Identity Mat'} ·{' '}
                        {s.sleeveCardName ? `Slv: ${s.sleeveCardName}` : '5-Pip Back'}
                      </div>
                    </div>
                    {canResetSeat && hasCustom && (
                      <button
                        type="button"
                        title="Reset seat to classic 5-pip sleeve & distinct identity mat"
                        onClick={() => onDispatch({ type: 'RESET_SEAT_COSMETICS', targetSeatId: sid })}
                        className="px-1.5 py-1 rounded bg-black/60 hover:bg-rose-950/90 border border-white/15 text-[9px] text-rose-200 shrink-0"
                      >
                        Reset
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
